import { useState } from 'react'
import { MessageCircle, Pencil, Trash2, Database } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { CopyableText } from '@/components/ui/CopyableText'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { getCountryName } from '@/lib/countries'
import { formatTimeAgo } from '@/lib/dateUtils'
import { CollapsibleSection } from './drawer/CollapsibleSection'
import { MainInfoSection } from './drawer/MainInfoSection'
import { DeviceSection } from './drawer/DeviceSection'
import { PageHistoryItem } from './drawer/PageHistoryItem'
import { getTimezoneOffset } from './drawer/utils'

export interface VisitorForDrawer {
  name: string
  email: string | null
  identified: boolean
  active: boolean
  live: boolean
  country_code: string | null
  last_seen_at: string | null
  current_page: { url: string; title: string | null } | null
  city?: string | null
  timezone?: string | null
  browser?: string | null
  os?: string | null
  referrer?: string | null
  page_history?: Array<{ url: string; title: string | null; at: string }>
  metadata?: Record<string, unknown> | null
  conversations?: Array<{ id: string; subject: string | null; status: string; last_message_at: string | null }>
  phone?: string | null
}

interface VisitorDetailDrawerProps {
  visitor: VisitorForDrawer
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: () => void
  onDelete?: () => void
}

export function VisitorDetailDrawer({
  visitor,
  open,
  onOpenChange,
  onEdit,
  onDelete,
}: VisitorDetailDrawerProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    main: true,
    device: true,
    pages: true,
    metadata: false,
    conversations: true,
  })

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const locationDisplay = [visitor.city, getCountryName(visitor.country_code)]
    .filter(Boolean)
    .join(', ')

  const localTime = visitor.timezone
    ? new Date().toLocaleTimeString('en-US', {
        timeZone: visitor.timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : null

  const timezoneOffset = visitor.timezone ? getTimezoneOffset(visitor.timezone) : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange} noOverlay>
      <SheetContent
        side="right"
        className="w-80 sm:w-96 p-0 flex flex-col bg-card overflow-hidden"
        hideCloseButton
      >
        <SheetHeader className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <VisitorAvatar
              name={visitor.name}
              identified={visitor.identified}
              active={visitor.active}
              live={visitor.live}
              countryCode={visitor.country_code}
              size="lg"
            />
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-medium truncate">
                {visitor.name}
              </SheetTitle>
              {visitor.email ? (
                <CopyableText
                  text={visitor.email}
                  className="text-sm text-muted-foreground max-w-full"
                  toastMessage="Email copied to clipboard"
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {visitor.identified ? 'Identified' : 'Anonymous'}
                </p>
              )}
            </div>
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit} className="size-8 p-0 cursor-pointer" title="Edit contact">
                <Pencil className="size-4" />
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <MainInfoSection
            locationDisplay={locationDisplay}
            countryCode={visitor.country_code}
            localTime={localTime}
            timezoneOffset={timezoneOffset}
            referrer={visitor.referrer}
            expanded={expandedSections.main}
            onToggle={() => toggleSection('main')}
          />

          <DeviceSection
            browser={visitor.browser}
            os={visitor.os}
            currentPage={visitor.current_page}
            expanded={expandedSections.device}
            onToggle={() => toggleSection('device')}
          />

          {visitor.page_history && visitor.page_history.length > 0 && (
            <CollapsibleSection
              title="Last browsed pages"
              expanded={expandedSections.pages}
              onToggle={() => toggleSection('pages')}
            >
              <div className="space-y-1">
                {visitor.page_history.map((page, index) => (
                  <PageHistoryItem key={index} page={page} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {visitor.metadata && Object.keys(visitor.metadata).length > 0 && (
            <CollapsibleSection
              title="Custom data"
              expanded={expandedSections.metadata}
              onToggle={() => toggleSection('metadata')}
            >
              <div className="space-y-2">
                {Object.entries(visitor.metadata).map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <div className="flex items-center h-5 shrink-0">
                      <Database className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-muted-foreground mr-2">{key}:</span>
                      <span className="text-foreground break-all">{String(value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {visitor.conversations && visitor.conversations.length > 0 && (
            <CollapsibleSection
              title={`Conversations (${visitor.conversations.length})`}
              expanded={expandedSections.conversations}
              onToggle={() => toggleSection('conversations')}
            >
              <div className="space-y-1">
                {visitor.conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center h-5 shrink-0">
                      <MessageCircle className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{conv.subject || 'No subject'}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className={conv.status === 'open' ? 'text-primary' : ''}>
                          {conv.status === 'open' ? 'Open' : 'Closed'}
                        </span>
                        {conv.last_message_at && (
                          <span>{formatTimeAgo(conv.last_message_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}
        </div>

        <div className="border-t border-border">
          {visitor.last_seen_at && (
            <div className="px-4 pt-3 pb-2 text-xs text-muted-foreground">
              Last seen {formatTimeAgo(visitor.last_seen_at)}
            </div>
          )}
          {onDelete && (
            <div className="px-4 pb-3">
              <ConfirmDialog
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 cursor-pointer"
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete contact
                  </Button>
                }
                title="Delete contact?"
                description="This will permanently delete this contact and all their data. This action cannot be undone."
                confirmText="Delete"
                destructive
                onConfirm={onDelete}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default VisitorDetailDrawer
